import { Field } from 'formik'
import { ChangeEvent, FC, useRef } from 'react'
import { StyleFormWrapper, StyleLabel } from '../style'
import {
  StyleFileAvatar,
  StyleFileAvatarIcon,
  StyleFileDashed,
  StyleFilePlaceholder,
  StyleGalleryCard,
  StyleUploadButton,
  StyleUploadButtonImgPreview,
  StyleUploadButtonWrapper,
  StyleUploadInput,
} from './style'
import Image from '@components/image'
import Row from '@components/row'
import SvgImage from '@components/svg'
import { StyleCircle } from '@components/styles'
import { Constant } from '@utils/index'
import Column from '@components/column'
import ErrorMessage from '../ErrorMessage'

interface UploadProps {
  varient?: 'button' | 'drag' | 'dashed' | 'galleryCard' | 'avatar'
  name: string
  placeholder?: string
  onChange?: (value: any) => void
  label?: string
  styleLabel?: string
  size?: number
  value?: any
  styleWrapper?: string
}

const Upload: FC<UploadProps> = ({
  varient = 'button',
  name,
  label,
  placeholder,
  onChange,
  size,
  styleLabel = '',
  value,
  styleWrapper,
}) => {
  const uploadInputRef = useRef<HTMLInputElement>(null)

  const handleChange = (form: any, file?: File) => {
    if (!file) return

    const fileName = file.name
    const extension = fileName
      .substring(fileName.lastIndexOf('.') + 1)
      .toLowerCase()

    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const base64String = e?.target?.result as string
      const [info, base64] = base64String.split(',')

      const fileData = {
        base64: base64.replace(/\s+/g, ''),
        extension,
        info,
      }

      form.setFieldValue(name, fileData)

      if (onChange) {
        onChange(fileData)
      }
    }
    reader.readAsDataURL(file)
  }

  return (
    <Field name={name}>
      {({ form, field, meta }: any) => {
        const hasError = meta.touched && meta.error

        const openFilePicker = () => {
          uploadInputRef.current?.click()
        }

        const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
          const file = event.target.files?.[0]
          handleChange(form, file)
          event.target.value = ''
        }

        const fileValue = field?.value

        return (
          <StyleFormWrapper styleWrapper={styleWrapper}>
            {!!label && varient !== 'drag' && (
              <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                {label}
              </StyleLabel>
            )}
            <StyleUploadInput
              type="file"
              ref={uploadInputRef}
              onChange={handleFileChange}
              accept="image/*"
            />
            {varient === 'button' && (
              <StyleUploadButtonWrapper onClick={openFilePicker}>
                <StyleUploadButton>
                  {placeholder || 'Browse File'}
                </StyleUploadButton>
                {!!fileValue && (
                  <StyleUploadButtonImgPreview
                    src={
                      fileValue?.base64
                        ? `${fileValue.info},${fileValue.base64}`
                        : fileValue
                    }
                  />
                )}
              </StyleUploadButtonWrapper>
            )}
            {varient === 'dashed' && (
              <StyleFileDashed
                hasError={hasError}
                onClick={openFilePicker}
                size={size || null}
              >
                {fileValue?.base64 ? (
                  <Image
                    customStyle={{ width: '100%', objectFit: 'contain' }}
                    src={`${fileValue.info},${fileValue.base64}`}
                    height={size || 100}
                  />
                ) : value ? (
                  <Image
                    customStyle={{ width: '100%', objectFit: 'contain' }}
                    height={size || 100}
                    src={value}
                  />
                ) : (
                  <Row colGap={10}>
                    <SvgImage src="upload" color="grey300" />
                    <StyleFilePlaceholder>{placeholder}</StyleFilePlaceholder>
                  </Row>
                )}
              </StyleFileDashed>
            )}
            {varient === 'avatar' && (
              <StyleFileAvatar
                hasError={hasError}
                onClick={openFilePicker}
                size={size || null}
              >
                {fileValue?.base64 ? (
                  <Image
                    customStyle={{
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: '50%',
                    }}
                    src={`${fileValue.info},${fileValue.base64}`}
                    height={size || 100}
                  />
                ) : value ? (
                  <Image
                    customStyle={{
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: '50%',
                    }}
                    height={size || 100}
                    src={value}
                  />
                ) : fileValue ? (
                  <Image
                    customStyle={{
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: '50%',
                    }}
                    height={size || 100}
                    src={`${process.env.BUCKET_URL}/${fileValue}`}
                  />
                ) : (
                  <Image
                    customStyle={{
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: '50%',
                    }}
                    height={size || 100}
                    src={Constant.defaultUser}
                  />
                )}
                <StyleFileAvatarIcon>
                  <SvgImage src="camera" color="grey300" />
                </StyleFileAvatarIcon>
              </StyleFileAvatar>
            )}
            {varient === 'galleryCard' && (
              <StyleGalleryCard
                hasError={hasError}
                onClick={openFilePicker}
                size={size || null}
              >
                {fileValue?.base64 ? (
                  <Image
                    customStyle={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: 8,
                    }}
                    src={`${fileValue.info},${fileValue.base64}`}
                  />
                ) : fileValue ? (
                  <Image
                    customStyle={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                      borderRadius: 8,
                    }}
                    src={`${process.env.BUCKET_URL}/${fileValue}`}
                  />
                ) : !!value ? (
                  <Image
                    customStyle={{ width: '100%', objectFit: 'contain' }}
                    src={value}
                  />
                ) : (
                  <Column alignItems="center" rowGap={10}>
                    <StyleCircle hasShadow>
                      <SvgImage size={18} src="galleryIcon" />
                    </StyleCircle>
                    <StyleFilePlaceholder>{placeholder}</StyleFilePlaceholder>
                  </Column>
                )}
              </StyleGalleryCard>
            )}
            {!!hasError && <ErrorMessage message={meta?.error} />}
          </StyleFormWrapper>
        )
      }}
    </Field>
  )
}

export default Upload
